<script lang="ts" setup>
import { useNuxtApp } from '#app';
import { computed, onMounted, onUnmounted, ref, useRoute, watch } from '#imports';

import { positions } from './animation';

type DropdownPosition = keyof typeof positions;

interface Props {
    isOpen: boolean;
    position?: DropdownPosition;
    dropdownClass?: string;
    dropdownContentClass?: string;
    customInitialPosition?: gsap.TweenVars;
    customToPosition?: gsap.TweenVars;
}

const TIME_ANIMATION = 0.21;
const EASE_ANIMATION = 'power2.out';

const refParent = ref<HTMLElement | undefined>(undefined);
const refContent = ref<Nullable<HTMLElement>>(null);
const isMounted = ref(false);

const emit = defineEmits(['update:isOpen']);

const props = withDefaults(
    defineProps<Props>(),
    {
        position: 'bottomLeft',
        dropdownClass: undefined,
        dropdownContentClass: undefined,
        customInitialPosition: undefined,
        customToPosition: undefined
    }
);

const { '$gsap': gsap } = useNuxtApp();
const ctx = ref<gsap.Context>();
const ddMenu = ref<GSAPTimeline>();

const contentClassByPosition = computed(() => props.position === 'headerFixed' || props.position === 'headerFixedAll' ? 'position-fixed' : 'position-absolute');

const gsapInitalStyles = computed(() => ({
    opacity: 0,
    boxShadow: 0,
    zIndex: -1,
    overflow: 'hidden',
    userSelect: 'none',
    pointerEvents: 'none',
    ...(props.position === 'headerFixed' || props.position === 'headerFixedAll' ? { height: 0 } : {}),
    ...props?.customInitialPosition || positions[props.position].initial
}));

const gsapToStyles = computed(() => ({
    userSelect: 'auto',
    opacity: 1,
    ...(props.position !== 'headerFixed' && props.position !== 'headerFixedAll' ? { overflowY: 'auto' } : {}),
    pointerEvents: 'auto',
    zIndex: 10,
    ...props?.customToPosition || positions[props.position].to
}));

function createAnimation() {
    ctx.value = gsap.context(
        () => {
            gsap.set(
                refContent.value,
                gsapInitalStyles.value
            );
            ddMenu.value = gsap
                ?.timeline({
                    defaults: {
                        duration: TIME_ANIMATION,
                        ease: EASE_ANIMATION
                    }
                })
                .to(
                    refContent.value,
                    gsapToStyles.value
                )
                .reverse();
        },
        refParent.value
    );
}

function handleClickOutside(event: MouseEvent) {
    if (refParent.value && refContent.value) {
        const targetNode = event.target as Node;
        if (!refParent.value.contains(targetNode) && !refContent.value.contains(targetNode)) {
            emit(
                'update:isOpen',
                false
            );
        }
    }
}

const route = useRoute();

watch(
    () => route.fullPath,
    () => emit(
        'update:isOpen',
        false
    )
);

watch(
    () => props.isOpen,
    (isOpen: boolean) => {
        const isOtherOpen = document.body.dataset.dd && document.body.dataset.dd !== randomKey.value;

        if (isOpen) {
            if (isOtherOpen) {
                const event = new CustomEvent('dropdown:closeOther', {
                    detail: { except: randomKey.value }
                });
                window.dispatchEvent(event);
            }

            ddMenu.value?.play();
        }
        else {
            ddMenu.value?.reverse();
        }
        handleBodyOverflow();
    }
);

const randomKey = ref<string>('');
const handleBodyOverflow = () => {
    if (props.position === 'headerFixed' || props.position === 'headerFixedAll') {
        const hidden = document.body.dataset.dd === randomKey.value;
        if (props.isOpen) {
            document.body.dataset.dd = randomKey.value;
            document.body.style.overflow = 'hidden';
        }
        else if (hidden) {
            delete document.body.dataset.dd;
            document.body.style.overflow = '';
        }
    }
};

function handleCloseOther(e: CustomEvent) {
    if (e.detail?.except !== randomKey.value) {
        emit('update:isOpen', false);
    }
}

onMounted(() => {
    randomKey.value = Math.random().toString(36)
        .substring(7);
    createAnimation();
    document.addEventListener(
        'click',
        handleClickOutside
    );
    window.addEventListener('dropdown:closeOther', handleCloseOther as EventListener);
    isMounted.value = true;
});

onUnmounted(() => {
    ctx.value?.revert();
    ctx.value = undefined;
    refParent.value = undefined;
    ddMenu.value?.kill();
    document.removeEventListener(
        'click',
        handleClickOutside
    );
    window.removeEventListener('dropdown:closeOther', handleCloseOther as EventListener);
});

</script>

<template>
  <div ref="refParent"
       class="position-relative"
       :class="dropdownClass"
       v-bind="$attrs"
  >
    <slot name="toggle"></slot>

    <div ref="refContent"
         class="dropdown-content"
         :class="[dropdownContentClass, contentClassByPosition]"
         @click.stop
    >
      <slot v-if="isMounted"
            name="content"
      ></slot>
    </div>
  </div>
</template>

<style lang="scss">
.dropdown {
  &-content {
    z-index: 0;
  }
}
</style>
